import { filter, equal } from '@ember/object/computed';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { belongsTo, hasMany } from 'ember-data/relationships';
import { computed } from '@ember/object';
import { isPresent } from '@ember/utils';
import { modelApiAction } from 'cio/utils/model-api-actions';
import CampaignModel from 'cio/models/campaign';
import ActionModel from 'cio/models/action';
import CustomerModel from 'cio/models/customer';
import SubjectStepModel from 'cio/models/subject-step';
import EventDataModel from './event-data';
import { htmlSafe } from '@ember/template';
import { SafeString } from 'handlebars';

export const STATE_LABELS: Record<string, { label: string; className: string; definition: string | SafeString }> = {
  init: {
    label: 'Starting',
    className: 'is-success',
    definition: "A person has matched the trigger but they haven't actually started their journey",
  },
  active: {
    label: 'Active',
    className: 'is-success',
    definition: 'A person is currently moving through the workflow',
  },
  inactive: {
    label: 'Leaving',
    className: 'is-pending',
    definition: htmlSafe(
      'A person is waiting in a <a href="https://customer.io/docs/grace-periods/" target="_blank" rel="noopener noreferrer">grace period</a>',
    ),
  },
  finished: {
    label: 'Finished',
    className: '',
    definition: 'A person has moved through the entire campaign workflow',
  },
  exited_early: {
    label: 'Exited Early',
    className: 'is-warning',
    definition: htmlSafe(
      'A person was removed from the campaign following a <a href="https://customer.io/docs/grace-periods/" target="_blank" rel="noopener noreferrer">grace period</a>, a manual removal, or a profile deletion',
    ),
  },
};

export default class SubjectModel extends Model {
  @attr('number') updated!: number;
  @attr('number') matchTime!: number;
  @attr('number') actionEntryTime!: number;
  @attr('number') nextWakeUp!: number;
  @attr('number') iteration!: number;
  @attr('string') subjectState!: string;
  @attr() eventData!: EventDataModel | undefined;
  @attr() event!: Record<string, unknown> | null; // nullable JSON object

  @belongsTo('campaign', { async: false, inverse: null }) campaign!: CampaignModel;
  @belongsTo('action', { async: false, inverse: null }) action!: ActionModel;
  @belongsTo('action', { async: false, inverse: null }) nextAction!: ActionModel;
  @belongsTo('customer', { async: false, inverse: 'subjects' }) customer!: CustomerModel;
  @hasMany('subject-step', { async: false, inverse: 'subject' }) subjectSteps!: SubjectStepModel[];

  @filter('subjectSteps', (step: SubjectStepModel) => {
    return !(step.get('future') || step.get('isHiddenStep'));
  })
  journeySteps!: SubjectStepModel[];
  @filter('subjectSteps', (step: SubjectStepModel) => {
    return step.get('future') && !step.get('isHiddenStep');
  })
  futureSteps!: SubjectStepModel[];

  @equal('subjectState', 'active') isActive!: boolean;
  @equal('subjectState', 'init') isStarting!: boolean;
  @equal('subjectState', 'inactive') isLeaving!: boolean;

  @computed('customer')
  get hasCustomer() {
    return isPresent((this as SubjectModel).belongsTo('customer').id());
  }

  forceExit = modelApiAction({
    path: 'force_exit',
    type: 'POST',
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    subject: SubjectModel;
  }
}
